import { parse, unparse } from 'papaparse';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';

import type { SourcePlatform } from '~entities/import-job/api/types';
import { useImportHeaderAliases } from '~entities/import-job/api/import-job-api';
import { buildFamilyColumnMappingTable } from '~features/import-wizard/build-family-column-mapping';
import { IMPORT_FAMILY_TEMPLATE_URL } from '~features/import-wizard/class-mapping-table-data';
import {
  getFamilyAliasesForPlatform,
  getProviderExportHeaders,
} from '~features/import-wizard/header-aliases-fallback';
import { ImportWizardCsvSelector } from '~features/import-wizard/import-wizard-csv-selector';
import {
  normalizeCsvHeaders,
  normalizeCsvRows,
} from '~features/import-wizard/parse-csv-headers';
import { downloadMappedCsv } from '~features/import-wizard/download-csv';
import { MappingGuidance } from '~features/import-wizard/mapping-guidance';
import { InvalidPhoneDialog } from '~features/import-wizard/invalid-phone-dialog';
import { collectInvalidPhones } from '~features/import-wizard/phone-validate';
import { PlatformMismatchWarning } from '~features/import-wizard/platform-mismatch-warning';
import { summarizeValidation } from '~features/import-wizard/validation-summary';
import { ValidationSummaryPanel } from '~features/import-wizard/validation-summary-panel';
import { applyPlatformFamilyRowTransforms } from '~features/import-wizard/platform-row-transforms';
import {
  headersFromRowKeys,
  RestoredCsvBanner,
} from '~features/import-wizard/restored-csv-banner';
import { ColumnMapping } from '~pages/parents/bulk-upload/column-mapping';
import { ValidateCSV } from '~pages/parents/bulk-upload/validate-csv';
import {
  type ParentRegistrationColumnMappingTableData,
  REQUIRED_PARENT_HEADERS,
} from '~pages/parents/bulk-upload/types';
import {
  type ParentRegistrationData,
  validateCell as validateParentCell,
} from '~pages/parents/bulk-upload/validation';
import { countParentGroupsFromCsvRows } from '~pages/parents/bulk-upload/count-parent-groups';

import { Button } from '~shared/shadcn/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~shared/shadcn/ui/select';

import type { DuplicatePolicy } from '~entities/import-job/api/types';

export interface FamilyWizardResult {
  file: File;
  rows: Array<ParentRegistrationData>;
  familyCount: number;
  duplicatePolicies: Record<string, DuplicatePolicy>;
}

interface Step3FamiliesProps {
  sourcePlatform: SourcePlatform;
  restoredResult?: FamilyWizardResult | null;
  onRestoredClear?: () => void;
  onComplete: (result: FamilyWizardResult | null) => void | Promise<void>;
  onBack: () => void;
  onSkip: () => void | Promise<void>;
}

function buildInitialFamilyState(restoredResult?: FamilyWizardResult | null) {
  if (!restoredResult?.file || restoredResult.rows.length === 0) {
    return {
      subStep: 'select' as const,
      file: null as File | null,
      csvData: [] as Array<ParentRegistrationData>,
      headers: [] as Array<string>,
      duplicatePolicies: {} as Record<string, DuplicatePolicy>,
      fromSession: false,
    };
  }
  return {
    subStep: 'validate' as const,
    file: restoredResult.file,
    csvData: restoredResult.rows,
    headers: headersFromRowKeys(restoredResult.rows),
    duplicatePolicies: restoredResult.duplicatePolicies ?? {},
    fromSession: true,
  };
}

export function Step3Families({
  sourcePlatform,
  restoredResult,
  onRestoredClear,
  onComplete,
  onBack,
  onSkip,
}: Step3FamiliesProps) {
  const initial = buildInitialFamilyState(restoredResult);
  const [subStep, setSubStepState] = useState<
    'select' | 'mapping' | 'validate'
  >(initial.subStep);
  const [file, setFile] = useState<File | null>(initial.file);
  const [csvData, setCSVData] = useState<Array<ParentRegistrationData>>(
    initial.csvData,
  );
  const [headers, setHeaders] = useState<Array<string>>(initial.headers);
  const [duplicatePolicies, setDuplicatePolicies] = useState<
    Record<string, DuplicatePolicy>
  >(initial.duplicatePolicies);
  const [fromSession, setFromSession] = useState(initial.fromSession);
  const [columnMappingTableData, setColumnMappingTableData] = useState<
    Array<ParentRegistrationColumnMappingTableData>
  >([]);
  const [phoneDialogOpen, setPhoneDialogOpen] = useState(false);
  const [phoneAcknowledged, setPhoneAcknowledged] = useState(false);
  const [continuing, setContinuing] = useState(false);
  const { data: headerAliases } = useImportHeaderAliases();
  const familyAliases = useMemo(
    () => getFamilyAliasesForPlatform(sourcePlatform, headerAliases),
    [headerAliases, sourcePlatform],
  );
  const expectedHeaders = useMemo(
    () => getProviderExportHeaders(sourcePlatform, headerAliases).families,
    [headerAliases, sourcePlatform],
  );

  const applyColumnPreMap = useCallback(
    (
      parsedHeaders: Array<string>,
      parsedCSVData: Array<ParentRegistrationData>,
    ) => {
      const csvRow = parsedCSVData[0];
      setColumnMappingTableData(
        buildFamilyColumnMappingTable(
          parsedHeaders,
          csvRow,
          familyAliases,
          sourcePlatform,
        ),
      );
    },
    [familyAliases, sourcePlatform],
  );

  useEffect(() => {
    if (subStep !== 'mapping' || headers.length === 0 || csvData.length === 0) {
      return;
    }
    applyColumnPreMap(headers, csvData);
  }, [subStep, headers, csvData, applyColumnPreMap, sourcePlatform, familyAliases]);

  const onFileChange = useCallback(
    (f: File | null) => {
      if (!f) return;
      setFromSession(false);
      setPhoneAcknowledged(false);
      onRestoredClear?.();
      setFile(f);
      parse<ParentRegistrationData>(f, {
        header: true,
        skipEmptyLines: true,
        complete: (result) => {
          const rawHeaders = result.meta.fields ?? [];
          const parsedHeaders = normalizeCsvHeaders(rawHeaders);
          const parsedCSVData = normalizeCsvRows(
            result.data,
            rawHeaders,
          );
          setCSVData(parsedCSVData);
          setHeaders(parsedHeaders);
          applyColumnPreMap(parsedHeaders, parsedCSVData);
        },
      });
    },
    [applyColumnPreMap, onRestoredClear],
  );

  const handleReplaceRestored = () => {
    setFromSession(false);
    setPhoneAcknowledged(false);
    onRestoredClear?.();
    setFile(null);
    setCSVData([]);
    setHeaders([]);
    setDuplicatePolicies({});
    setColumnMappingTableData([]);
    setSubStepState('select');
  };

  const setSubStep = (next: 'select' | 'mapping' | 'validate') => {
    setSubStepState(next);
  };

  const parentEmails = [
    ...new Set(
      csvData
        .map((r) => String(r['Parent Email'] ?? '').trim().toLowerCase())
        .filter(Boolean),
    ),
  ];

  const invalidPhones = useMemo(
    () => collectInvalidPhones(csvData as Array<Record<string, unknown>>),
    [csvData],
  );

  const validationSummary = useMemo(
    () =>
      summarizeValidation(
        csvData as Array<Record<string, unknown>>,
        (key, value) =>
          validateParentCell(key as keyof ParentRegistrationData, value),
      ),
    [csvData],
  );

  const handleContinueClick = () => {
    // Per QA: surface invalid phone formats and require an explicit
    // acknowledgment so studios have a chance to fix their CSV first.
    if (invalidPhones.length > 0 && !phoneAcknowledged) {
      setPhoneDialogOpen(true);
      return;
    }
    void handleFinish();
  };

  const handleFinish = async () => {
    setContinuing(true);
    try {
      if (!file) {
        await onComplete(null);
        return;
      }
      const transformedRows = applyPlatformFamilyRowTransforms(
        csvData,
        sourcePlatform,
      );
      const csvString = unparse(transformedRows);
      const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
      const outFile = new File([blob], file.name || 'families-import.csv', {
        type: 'text/csv',
      });
      await onComplete({
        file: outFile,
        rows: transformedRows,
        familyCount: countParentGroupsFromCsvRows(transformedRows),
        duplicatePolicies,
      });
    } finally {
      setContinuing(false);
    }
  };

  return (
    <div className='space-y-4'>
      <div className='flex items-center justify-between'>
        <h2 className='text-xl font-semibold'>Your families</h2>
        <Button
          variant='ghost'
          size='sm'
          onClick={onSkip}
          disabled={continuing}
        >
          Skip families
        </Button>
      </div>
      {expectedHeaders.length > 0 && (
        <p className='text-sm text-muted-foreground'>
          Expected columns for your provider:{' '}
          <span className='font-medium text-foreground'>
            {expectedHeaders.join(', ')}
          </span>
        </p>
      )}
      {fromSession && file && (
        <RestoredCsvBanner
          filename={file.name}
          rowCount={csvData.length}
          datasetLabel='family row'
          onReplace={handleReplaceRestored}
        />
      )}
      {subStep === 'select' && (
        <>
          <ImportWizardCsvSelector
            file={file}
            setFile={onFileChange}
            title='Upload your family list'
            description='Parent and student columns from the Enrollio template (including address and relation fields) are selected automatically on the next step.'
            sampleCsvUrl={IMPORT_FAMILY_TEMPLATE_URL}
            sampleCsvDownloadName='import-families-template.csv'
            onNext={() => {
              if (headers.length > 0 && csvData.length > 0) {
                applyColumnPreMap(headers, csvData);
              }
              setSubStep('mapping');
            }}
          />
          <div className='flex flex-wrap justify-start gap-2'>
            <Button variant='outline' onClick={onBack}>
              Back
            </Button>
            {fromSession && csvData.length > 0 && (
              <Button variant='secondary' onClick={() => setSubStep('validate')}>
                Review restored data
              </Button>
            )}
          </div>
        </>
      )}
      {subStep === 'mapping' && (
        <>
          <MappingGuidance
            datasetLabel='family'
            requiredFields={REQUIRED_PARENT_HEADERS}
          />
          <PlatformMismatchWarning
            sourcePlatform={sourcePlatform}
            uploadedHeaders={headers}
            expectedHeaders={expectedHeaders}
            datasetLabel='family'
          />
          <ColumnMapping
            setStep={setSubStep}
            columnHeaders={headers}
            columnMappingTableData={columnMappingTableData}
            setColumnMappingTableData={setColumnMappingTableData}
            csvData={csvData}
            setCSVData={setCSVData}
          />
        </>
      )}
      {subStep === 'validate' && (
        <>
          <ValidationSummaryPanel
            summary={validationSummary}
            totalRows={csvData.length}
          />
          <ValidateCSV csvData={csvData} embedded />
          {parentEmails.length > 0 && (
            <div className='space-y-3 rounded-md border p-4'>
              <div className='flex flex-wrap items-center justify-between gap-2'>
                <div>
                  <p className='text-sm font-medium'>
                    If a family email already exists in your studio
                  </p>
                  <p className='text-sm text-muted-foreground'>
                    Choose what to do for each of the {parentEmails.length}{' '}
                    email{parentEmails.length === 1 ? '' : 's'} (default: skip
                    the row).
                  </p>
                </div>
                <div className='flex items-center gap-2 text-sm'>
                  <span className='text-muted-foreground'>Apply to all</span>
                  <Select
                    onValueChange={(v) =>
                      setDuplicatePolicies(
                        Object.fromEntries(
                          parentEmails.map((email) => [
                            email,
                            v as DuplicatePolicy,
                          ]),
                        ),
                      )
                    }
                  >
                    <SelectTrigger className='w-36'>
                      <SelectValue placeholder='Choose…' />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='skip'>Skip</SelectItem>
                      <SelectItem value='merge'>Merge students</SelectItem>
                      <SelectItem value='update'>Update & merge</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className='max-h-72 space-y-2 overflow-y-auto pr-1'>
                {parentEmails.map((email) => (
                  <div
                    key={email}
                    className='flex flex-wrap items-center justify-between gap-2 text-sm'
                  >
                    <span className='truncate'>{email}</span>
                    <Select
                      value={duplicatePolicies[email] ?? 'skip'}
                      onValueChange={(v) =>
                        setDuplicatePolicies((prev) => ({
                          ...prev,
                          [email]: v as DuplicatePolicy,
                        }))
                      }
                    >
                      <SelectTrigger className='w-36'>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value='skip'>Skip</SelectItem>
                        <SelectItem value='merge'>Merge students</SelectItem>
                        <SelectItem value='update'>Update & merge</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className='flex flex-wrap items-center justify-between gap-2'>
            <Button
              variant='outline'
              onClick={() => setSubStep('mapping')}
              disabled={continuing}
            >
              Back
            </Button>
            <div className='flex flex-wrap gap-2'>
              <Button
                variant='secondary'
                disabled={continuing}
                onClick={() =>
                  downloadMappedCsv(
                    applyPlatformFamilyRowTransforms(
                      csvData,
                      sourcePlatform,
                    ) as Array<Record<string, unknown>>,
                    'mapped-families.csv',
                  )
                }
              >
                Download mapped CSV
              </Button>
              <Button onClick={handleContinueClick} disabled={continuing}>
                {continuing ? (
                  <>
                    <Loader2 className='mr-2 size-4 animate-spin' />
                    Saving…
                  </>
                ) : (
                  'Continue'
                )}
              </Button>
            </div>
          </div>
          <InvalidPhoneDialog
            open={phoneDialogOpen}
            onOpenChange={setPhoneDialogOpen}
            entries={invalidPhones}
            onAcknowledge={() => {
              setPhoneAcknowledged(true);
              setPhoneDialogOpen(false);
              void handleFinish();
            }}
          />
        </>
      )}
    </div>
  );
}
